import * as React from 'react'
import { Bold, Italic, List } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export function RichTextEditor({
  id,
  'aria-label': ariaLabel,
  value,
  onChange,
  className,
}: {
  id?: string
  'aria-label'?: string
  value: string
  onChange: (value: string) => void
  className?: string
}) {
  const editorRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value)
      editorRef.current.innerHTML = value
  }, [value])

  const format = (command: 'bold' | 'italic' | 'insertUnorderedList') => {
    editorRef.current?.focus()
    document.execCommand(command)
    onChange(editorRef.current?.innerHTML ?? '')
  }

  return (
    <div className={cn('overflow-hidden rounded-md border border-input bg-popover', className)}>
      <div className="flex gap-1 border-b border-border p-1" aria-label="Description formatting">
        <FormatButton label="Bold" onClick={() => format('bold')}>
          <Bold className="h-3.5 w-3.5" />
        </FormatButton>
        <FormatButton label="Italic" onClick={() => format('italic')}>
          <Italic className="h-3.5 w-3.5" />
        </FormatButton>
        <FormatButton label="Bulleted list" onClick={() => format('insertUnorderedList')}>
          <List className="h-3.5 w-3.5" />
        </FormatButton>
      </div>
      <div
        ref={editorRef}
        id={id}
        aria-label={ariaLabel}
        role="textbox"
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        className="min-h-24 px-3 py-2 text-body-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        onInput={(event) => onChange(event.currentTarget.innerHTML)}
      />
    </div>
  )
}

function FormatButton({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 w-8 border-transparent p-0"
      aria-label={label}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}
